import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/** Danger zone: permanent account deletion. Deletion cancels Pro Plus billing
 *  automatically in our backend — the user is never told to cancel in Stripe
 *  first. The pre-confirmation text comes from the server so it always matches
 *  the account's real billing state (paid / trial / entitlement / free). */
export default function DeleteAccountPanel() {
  const { logout } = useAuth();
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    try {
      const r = await api.get("/account/deletion/preview");
      setPreview(r.data);
      setOpen(true);
      setTyped("");
    } catch {
      toast.error("Could not load account details");
    }
  };

  const confirm = async () => {
    if (typed.trim().toUpperCase() !== "DELETE" || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/account/deletion/request");
      if (r.data?.status === "completed") {
        toast.success("Your account has been deleted.");
        setTimeout(() => { logout(); window.location.href = "/"; }, 1200);
      } else {
        // Never claim a completed deletion/cancellation that Stripe hasn't confirmed.
        toast.error(
          "We've recorded your deletion request, but we couldn't confirm the billing "
          + "cancellation with our payment processor yet. Our team has been alerted and "
          + "will finish it — you will not be charged again."
        );
        setOpen(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete the account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-black/[0.08]" data-testid="danger-zone">
      <h3 className="text-sm font-bold text-red-700 flex items-center gap-2 mb-1">
        <AlertTriangle size={15}/> Danger zone
      </h3>
      <p className="text-sm text-[#222]/60 mb-3">
        Deleting your account ends your access immediately and automatically cancels any
        Pro Plus membership — you don't need to cancel anywhere else first.
      </p>
      <button onClick={start} className="px-4 py-2 text-sm rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition flex items-center gap-2" data-testid="delete-account-btn">
        <Trash2 size={14}/> Delete my account
      </button>

      {open && preview && (
        <div className="fixed inset-0 bg-black/45 z-50 grid place-items-center px-4" data-testid="delete-account-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[88vh] overflow-y-auto">
            <h4 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Delete your account?</h4>
            <p className="text-xs uppercase tracking-wider font-semibold text-[#222]/45 mb-3" data-testid="delete-account-kind">
              Account type: {preview.account_kind.replace(/_/g, " ")}
            </p>
            <ul className="space-y-2 mb-4">
              {preview.explanation.map((line, i) => (
                <li key={i} className="text-sm text-[#222]/75 leading-relaxed flex gap-2" data-testid={`delete-explain-${i}`}>
                  <span className="text-red-500 mt-0.5">•</span><span>{line}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#222]/55 mb-4">
              See the <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline text-[#245544]">Privacy Policy</a> for
              what we must retain, and the <a href="/legal/membership" target="_blank" rel="noreferrer" className="underline text-[#245544]">Membership &amp; Billing Terms</a> for
              the refund policy.
            </p>
            <label className="text-sm font-semibold block mb-1">Type DELETE to confirm</label>
            <input value={typed} onChange={e => setTyped(e.target.value)}
              className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm mb-4 outline-none focus:ring-2 focus:ring-red-300"
              placeholder="DELETE" data-testid="delete-account-confirm-input"/>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-full border border-black/10" data-testid="delete-account-cancel">Keep my account</button>
              <button onClick={confirm} disabled={busy || typed.trim().toUpperCase() !== "DELETE"}
                className="px-4 py-2 text-sm rounded-full bg-red-600 text-white font-semibold disabled:opacity-40 flex items-center gap-2"
                data-testid="delete-account-confirm">
                {busy ? <><Loader2 size={14} className="animate-spin"/> Deleting…</> : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
